import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { FeatDefinition } from '@character-creator/types';
import { useRulesetFeats } from '../hooks/useRulesetData';
import { LinkedText } from '../components/LinkedText';

function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function FeatsReferencePage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFeatId, setExpandedFeatId] = useState<string | null>(null);
  const feats = useRulesetFeats();

  const filteredFeats = useMemo(() => {
    if (!searchQuery) return feats;

    const query = searchQuery.toLowerCase();
    return feats.filter(
      (feat) =>
        feat.name.toLowerCase().includes(query) ||
        feat.description.toLowerCase().includes(query) ||
        feat.benefits.some((b) => b.toLowerCase().includes(query))
    );
  }, [searchQuery, feats]);

  const toggleFeatExpanded = (featId: string) => {
    setExpandedFeatId((prev) => (prev === featId ? null : featId));
  };

  return (
    <div className="min-h-screen bg-background-primary">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-background-card/95 backdrop-blur-sm border-b border-border-default">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link
              to="/create"
              className="p-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-background-elevated transition-colors"
            >
              <BackIcon className="w-5 h-5" />
            </Link>
            <h1 className="text-xl font-semibold text-text-primary">Feats Reference</h1>
          </div>
        </div>
      </header>

      {/* Search */}
      <div className="sticky top-[65px] z-10 bg-background-primary border-b border-border-default">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
            <input
              type="text"
              placeholder="Search feats..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-background-card border border-border-default rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent-gold"
            />
          </div>
        </div>
      </div>

      {/* Intro Text */}
      <div className="max-w-4xl mx-auto px-4 py-4">
        <p className="text-sm text-text-secondary">
          A feat represents a talent or an area of expertise that gives a character special
          capabilities. It embodies training, experience, and abilities beyond what a class
          provides. At certain levels, your class gives you the Ability Score Improvement feature.
          Using the optional feats rule, you can forgo taking that feature to take a feat of your
          choice instead.
        </p>
        <p className="text-sm text-text-muted mt-2">
          Showing {filteredFeats.length} of {feats.length} feats
        </p>
        <p className="text-xs text-text-muted mt-1 italic">
          Note: The SRD 5.1 only includes the Grappler feat. Additional feats require licensed
          content.
        </p>
      </div>

      {/* Feat List */}
      <main className="max-w-4xl mx-auto px-4 pb-8">
        {filteredFeats.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-card flex items-center justify-center border border-border-default">
              <FeatIcon className="w-8 h-8 text-text-muted" />
            </div>
            <p className="text-text-secondary">No feats match your search.</p>
            <button
              onClick={() => setSearchQuery('')}
              className="mt-4 px-4 py-2 text-accent-gold hover:underline"
            >
              Clear search
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredFeats.map((feat) => (
              <FeatCard
                key={feat.id}
                feat={feat}
                isExpanded={expandedFeatId === feat.id}
                onToggle={() => toggleFeatExpanded(feat.id)}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

interface FeatCardProps {
  feat: FeatDefinition;
  isExpanded: boolean;
  onToggle: () => void;
}

function FeatCard({ feat, isExpanded, onToggle }: FeatCardProps) {
  const hasPrerequisites = feat.prerequisites !== undefined;

  return (
    <div
      id={feat.id}
      className="bg-background-card border border-border-default rounded-lg overflow-hidden"
    >
      {/* Collapsed View */}
      <button
        onClick={onToggle}
        className="w-full p-4 text-left hover:bg-background-elevated transition-colors"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-text-primary">{feat.name}</h3>
              {hasPrerequisites && (
                <span className="px-1.5 py-0.5 text-xs font-medium bg-accent-orange/20 text-accent-orange rounded">
                  Has Prerequisites
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-text-muted line-clamp-2">
              <LinkedText excludePages={['feats']}>{feat.description}</LinkedText>
            </p>
          </div>
          <div className="shrink-0 text-right">
            <span className="text-xs text-text-muted">{feat.source}</span>
          </div>
        </div>
        <div className="flex items-center justify-center mt-2">
          <ChevronIcon
            className={`w-5 h-5 text-text-muted transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {/* Expanded View */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-border-default">
          <div className="pt-4 space-y-4">
            {/* Prerequisites */}
            {feat.prerequisites && (
              <div>
                <h4 className="text-sm font-semibold text-text-primary mb-2">Prerequisites</h4>
                <div className="space-y-1">
                  {feat.prerequisites.abilityScores &&
                    Object.entries(feat.prerequisites.abilityScores).map(([ability, score]) => (
                      <p key={ability} className="text-sm text-text-secondary">
                        {capitalizeFirst(ability)} {score} or higher
                      </p>
                    ))}
                  {feat.prerequisites.proficiency && (
                    <p className="text-sm text-text-secondary">
                      Proficiency with {feat.prerequisites.proficiency}
                    </p>
                  )}
                  {feat.prerequisites.level && (
                    <p className="text-sm text-text-secondary">
                      Character level {feat.prerequisites.level} or higher
                    </p>
                  )}
                  {feat.prerequisites.race && (
                    <p className="text-sm text-text-secondary">{feat.prerequisites.race}</p>
                  )}
                  {feat.prerequisites.class && (
                    <p className="text-sm text-text-secondary">{feat.prerequisites.class} class</p>
                  )}
                  {feat.prerequisites.spellcasting && (
                    <p className="text-sm text-text-secondary">
                      The ability to cast at least one spell
                    </p>
                  )}
                  {feat.prerequisites.other && (
                    <p className="text-sm text-text-secondary">{feat.prerequisites.other}</p>
                  )}
                </div>
              </div>
            )}

            {/* Benefits */}
            <div>
              <h4 className="text-sm font-semibold text-text-primary mb-2">Benefits</h4>
              <ul className="space-y-1.5">
                {feat.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-text-secondary">
                    <span className="text-accent-gold mt-1">*</span>
                    <LinkedText excludePages={['feats']}>{benefit}</LinkedText>
                  </li>
                ))}
              </ul>
            </div>

            {/* Ability Score Increase */}
            {feat.abilityScoreIncrease && (
              <div>
                <h4 className="text-sm font-semibold text-text-primary mb-2">
                  Ability Score Increase
                </h4>
                <p className="text-sm text-text-secondary">
                  {feat.abilityScoreIncrease.choices
                    ? `Increase ${feat.abilityScoreIncrease.choices.map(capitalizeFirst).join(' or ')} by ${feat.abilityScoreIncrease.amount || 1}`
                    : `Increase an ability score by ${feat.abilityScoreIncrease.amount || 1}`}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function BackIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  );
}

function FeatIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
      />
    </svg>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}
